import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { AlertTriangle, ExternalLink, Globe2, Layers3, Radio, X } from 'lucide-react';
import Header from '../components/Header';
import MapView from '../components/MapView';
import LeftSidebar from '../components/LeftSidebar';
import NewsFeed from '../components/NewsFeed';
import OnboardingModal from '../components/OnboardingModal';
import CookieBanner from '../components/CookieBanner';
import { CLASSIFICATIONS, DISEASES, SIGNALS as MOCK_SIGNALS } from '../mock';

const DEFAULT_DATA_BASE_URL = 'https://pub-7d83f90720174d89aa9e0c2bb9ca2122.r2.dev/data';
const DATA_BASE_URL = (process.env.REACT_APP_DATA_BASE_URL || DEFAULT_DATA_BASE_URL).replace(/\/$/, '');
const STATIC_SIGNALS_URL = `${DATA_BASE_URL}/signals.json`;
const SIGNAL_CACHE_KEY = 'outbreakmap_cached_signals_v1';
const SIGNAL_CACHE_TTL_MS = 55 * 60 * 1000;
const LS_KEY = 'outbreakmap_state_v2';

const TIME_WINDOWS = {
  '24h': { label: '24h', hours: 24 },
  '7d': { label: '7d', hours: 24 * 7 },
  '30d': { label: '30d', hours: 24 * 30 },
  all: { label: 'All', hours: Infinity },
};

const formatAgo = (ms) => {
  const s = Math.max(1, Math.floor(ms / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min${m === 1 ? '' : 's'} ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.floor(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ago`;
};

const getSignalAgeHours = (signal, now = Date.now()) => {
  if (Number.isFinite(Number(signal.hoursAgo))) return Number(signal.hoursAgo);
  const ts = new Date(signal.publishedAt || 0).getTime();
  if (!Number.isFinite(ts)) return Infinity;
  return Math.max(0, Math.floor((now - ts) / 3600000));
};

const countBy = (items, getKey) => items.reduce((acc, item) => {
  const key = getKey(item) || 'Unknown';
  acc[key] = (acc[key] || 0) + 1;
  return acc;
}, {});

const topEntries = (counts, limit = 6) => (
  Object.entries(counts).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).slice(0, limit)
);

const OutbreakMapPage = () => {
  const persisted = (() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}'); } catch { return {}; }
  })();

  const [activeDiseases, setActiveDiseases] = useState(
    persisted.activeDiseases || DISEASES.filter(d => d.enabled).map(d => d.id)
  );
  const [classFilter, setClassFilter] = useState(
    persisted.classFilter || { official: true, emerging: true, chatter: true }
  );
  const [timeWindow, setTimeWindow] = useState(persisted.timeWindow || 'all');
  const [sourceFilter, setSourceFilter] = useState(persisted.sourceFilter || 'all');
  const [countryFocus, setCountryFocus] = useState(persisted.countryFocus || 'all');
  const [query, setQuery] = useState('');
  const [selectedSignal, setSelectedSignal] = useState(null);
  const [onboardingOpen, setOnboardingOpen] = useState(!persisted.seenOnboarding);
  const [cookieOpen, setCookieOpen] = useState(!persisted.cookieChoice);

  const [signals, setSignals] = useState(MOCK_SIGNALS);
  const [snapshotStats, setSnapshotStats] = useState(null);
  const [sources, setSources] = useState([]);
  const [usingMock, setUsingMock] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(Date.now());
  const [currentTime, setCurrentTime] = useState(Date.now());

  const applySignalPayload = useCallback((data) => {
    if (data && Array.isArray(data.signals) && data.signals.length > 0) {
      setSignals(data.signals);
      setSnapshotStats(data.stats || null);
      setSources(Array.isArray(data.sources) ? data.sources : []);
      setUsingMock(false);
      if (data.lastUpdated) {
        const ts = new Date(data.lastUpdated).getTime();
        if (!Number.isNaN(ts)) setLastUpdated(ts);
      } else {
        setLastUpdated(Date.now());
      }
      return true;
    }
    return false;
  }, []);

  const loadSignals = useCallback(async () => {
    try {
      const cached = JSON.parse(localStorage.getItem(SIGNAL_CACHE_KEY) || 'null');
      if (cached?.savedAt && Date.now() - cached.savedAt < SIGNAL_CACHE_TTL_MS && applySignalPayload(cached.payload)) {
        return;
      }
    } catch {
      localStorage.removeItem(SIGNAL_CACHE_KEY);
    }

    try {
      const hourBucket = Math.floor(Date.now() / (60 * 60 * 1000));
      const res = await axios.get(STATIC_SIGNALS_URL, { params: { v: hourBucket } });
      const data = res.data;
      if (applySignalPayload(data)) {
        localStorage.setItem(SIGNAL_CACHE_KEY, JSON.stringify({ savedAt: Date.now(), payload: data }));
      }
    } catch (e) {
      console.warn('Cached signals fetch failed; using demo data', e?.message);
    }
  }, [applySignalPayload]);

  useEffect(() => {
    loadSignals();
  }, [loadSignals]);

  useEffect(() => {
    const t = setInterval(() => setCurrentTime(Date.now()), 15000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const refreshTimer = setInterval(loadSignals, 1000 * 60 * 60);
    return () => clearInterval(refreshTimer);
  }, [loadSignals]);

  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify({
      activeDiseases,
      classFilter,
      timeWindow,
      sourceFilter,
      countryFocus,
      seenOnboarding: !onboardingOpen ? true : persisted.seenOnboarding,
      cookieChoice: persisted.cookieChoice || (!cookieOpen ? 'accept' : null),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDiseases, classFilter, timeWindow, sourceFilter, countryFocus, onboardingOpen, cookieOpen]);

  const sourceOptions = useMemo(() => {
    const names = new Set(signals.map(signal => signal.source).filter(Boolean));
    return ['all', ...[...names].sort()];
  }, [signals]);

  const countryOptions = useMemo(() => {
    const names = new Set(signals.map(signal => signal.country || signal.place).filter(Boolean));
    return ['all', ...[...names].sort()];
  }, [signals]);

  const filteredSignals = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const maxAge = TIME_WINDOWS[timeWindow]?.hours ?? Infinity;

    return signals.filter(signal => {
      const disease = DISEASES.find(item => item.id === signal.diseaseId);
      const haystack = [signal.headline, signal.place, signal.country, signal.source, disease?.name, disease?.short]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return activeDiseases.includes(signal.diseaseId)
        && classFilter[signal.classification]
        && getSignalAgeHours(signal, currentTime) <= maxAge
        && (sourceFilter === 'all' || signal.source === sourceFilter)
        && (countryFocus === 'all' || signal.country === countryFocus || signal.place === countryFocus)
        && (!needle || haystack.includes(needle));
    });
  }, [activeDiseases, classFilter, countryFocus, currentTime, query, signals, sourceFilter, timeWindow]);

  const dashboard = useMemo(() => {
    const countries = new Set(filteredSignals.map(signal => signal.country).filter(Boolean));
    const recent24h = filteredSignals.filter(signal => getSignalAgeHours(signal, currentTime) <= 24).length;
    const diseaseCounts = topEntries(countBy(filteredSignals, signal => DISEASES.find(d => d.id === signal.diseaseId)?.name || signal.diseaseId), 8);
    const countryCounts = topEntries(countBy(filteredSignals, signal => signal.country || signal.place), 8);
    const sourceCounts = topEntries(countBy(filteredSignals, signal => signal.source), 8);
    const classCounts = Object.keys(CLASSIFICATIONS).reduce((acc, id) => {
      acc[id] = filteredSignals.filter(signal => signal.classification === id).length;
      return acc;
    }, {});
    return {
      signalCount: filteredSignals.length,
      countryCount: countries.size,
      recent24h,
      diseaseCounts,
      countryCounts,
      sourceCounts,
      classCounts,
      topCountry: countryCounts[0] || null,
      topDisease: diseaseCounts[0] || null,
    };
  }, [currentTime, filteredSignals]);

  const selectedRelated = useMemo(() => {
    if (!selectedSignal) return [];
    return filteredSignals
      .filter(signal => signal.id !== selectedSignal.id && signal.country === selectedSignal.country)
      .sort((a, b) => getSignalAgeHours(a, currentTime) - getSignalAgeHours(b, currentTime))
      .slice(0, 5);
  }, [currentTime, filteredSignals, selectedSignal]);

  const lastUpdatedLabel = formatAgo(currentTime - lastUpdated);
  const snapshotAgeMs = currentTime - lastUpdated;
  const isSnapshotStale = snapshotAgeMs > 2 * 60 * 60 * 1000;

  const resetFilters = () => {
    setActiveDiseases(DISEASES.filter(d => d.enabled).map(d => d.id));
    setClassFilter({ official: true, emerging: true, chatter: true });
    setTimeWindow('all');
    setSourceFilter('all');
    setCountryFocus('all');
    setQuery('');
  };

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-[#040c0e]">
      <MapView
        signals={filteredSignals}
        activeDiseases={activeDiseases}
        classFilter={classFilter}
        onSelectSignal={setSelectedSignal}
      />

      <div className="pointer-events-none absolute inset-0 z-10 map-vignette" />

      <Header
        lastUpdatedLabel={lastUpdatedLabel}
        signalCount={dashboard.signalCount}
        countryCount={dashboard.countryCount}
        recentCount={dashboard.recent24h}
        isSnapshotStale={isSnapshotStale}
        onOpenAbout={() => setOnboardingOpen(true)}
      />

      <LeftSidebar
        activeDiseases={activeDiseases}
        setActiveDiseases={setActiveDiseases}
        classFilter={classFilter}
        setClassFilter={setClassFilter}
        timeWindow={timeWindow}
        setTimeWindow={setTimeWindow}
        sourceFilter={sourceFilter}
        setSourceFilter={setSourceFilter}
        countryFocus={countryFocus}
        setCountryFocus={setCountryFocus}
        sourceOptions={sourceOptions}
        countryOptions={countryOptions}
        sources={sources}
        snapshotStats={snapshotStats}
        usingMock={usingMock}
        lastUpdatedLabel={lastUpdatedLabel}
        dashboard={dashboard}
        onResetFilters={resetFilters}
      />

      <NewsFeed
        signals={filteredSignals}
        activeDiseases={activeDiseases}
        query={query}
        setQuery={setQuery}
        sourceFilter={sourceFilter}
        setSourceFilter={setSourceFilter}
        countryFocus={countryFocus}
        setCountryFocus={setCountryFocus}
        onSelectSignal={setSelectedSignal}
        lastUpdatedLabel={lastUpdatedLabel}
        dashboard={dashboard}
        sourceOptions={sourceOptions}
        countryOptions={countryOptions}
      />

      <div className={`status-pill absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2 rounded-full border px-3 py-1.5 text-[11.5px] backdrop-blur-md ${isSnapshotStale ? 'border-amber-500/30 bg-amber-950/60 text-amber-100' : 'border-cyan-900/40 bg-[#08191d]/90 text-cyan-200'}`}>
        <span className="relative flex h-1.5 w-1.5">
          <span className={`absolute inline-flex h-full w-full rounded-full opacity-75 ${isSnapshotStale ? 'bg-amber-400' : 'animate-ping bg-emerald-400'}`} />
          <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${isSnapshotStale ? 'bg-amber-400' : 'bg-emerald-400'}`} />
        </span>
        <span className={isSnapshotStale ? 'font-medium text-amber-200' : 'font-medium text-emerald-300'}>{isSnapshotStale ? 'Delayed' : 'Live'}</span>
        <span className="text-cyan-800">·</span>
        <span>Updated {lastUpdatedLabel}</span>
        {usingMock && (
          <>
            <span className="text-cyan-800">·</span>
            <span className="text-amber-300/90">demo data</span>
          </>
        )}
      </div>

      <div className="legal-note absolute bottom-1.5 right-4 z-20 text-[10px] text-cyan-800/80">
        OutbreakMap &copy; 2026 · Informational only · Not medical advice
      </div>

      <OnboardingModal open={onboardingOpen} onClose={() => setOnboardingOpen(false)} />

      <CookieBanner
        open={cookieOpen && !onboardingOpen}
        onAccept={() => { localStorage.setItem(LS_KEY, JSON.stringify({ ...persisted, cookieChoice: 'accept' })); setCookieOpen(false); }}
        onReject={() => { localStorage.setItem(LS_KEY, JSON.stringify({ ...persisted, cookieChoice: 'reject' })); setCookieOpen(false); }}
      />

      {selectedSignal && (
        <div className="detail-card fade-in absolute left-[280px] top-[78px] z-30 w-[370px] rounded-lg border border-cyan-700/40 bg-[#071b20]/95 p-4 backdrop-blur-md shadow-[0_18px_50px_rgba(0,0,0,0.62)]">
          <div className="mb-3 flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] text-cyan-500">
                <Radio className="h-3 w-3" /> Signal detail
              </div>
              <div className="mt-1 text-[13.5px] font-semibold leading-snug text-cyan-50">{selectedSignal.headline}</div>
            </div>
            <button onClick={() => setSelectedSignal(null)} className="rounded-md p-1 text-cyan-700 hover:bg-cyan-500/10 hover:text-cyan-100" aria-label="Close signal detail">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-3 gap-2 border-y border-cyan-900/40 py-3 text-[11px]">
            <span className="text-cyan-600">Class <b className="block font-semibold text-cyan-100">{CLASSIFICATIONS[selectedSignal.classification]?.label}</b></span>
            <span className="text-cyan-600">Age <b className="block font-semibold text-cyan-100">{getSignalAgeHours(selectedSignal, currentTime)}h</b></span>
            <span className="text-cyan-600">Source <b className="block truncate font-semibold text-cyan-100">{selectedSignal.source}</b></span>
          </div>

          <div className="mt-3 grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 text-[11.5px] text-cyan-300/85">
            <Globe2 className="mt-0.5 h-3.5 w-3.5 text-cyan-600" />
            <span>{selectedSignal.place}{selectedSignal.country && selectedSignal.place !== selectedSignal.country ? `, ${selectedSignal.country}` : ''}</span>
            <Layers3 className="mt-0.5 h-3.5 w-3.5 text-cyan-600" />
            <span>{DISEASES.find(d => d.id === selectedSignal.diseaseId)?.name || selectedSignal.diseaseId}</span>
          </div>

          {selectedSignal.extra && (
            <div className="mt-3 grid grid-cols-3 gap-2">
              {Object.entries(selectedSignal.extra).slice(0, 3).map(([key, value]) => (
                <div key={key} className="rounded-md border border-cyan-900/40 bg-cyan-400/[0.04] px-2 py-1.5">
                  <div className="text-[9px] uppercase tracking-[0.12em] text-cyan-700">{key}</div>
                  <div className="truncate text-[12px] font-semibold text-cyan-50">{Number.isFinite(value) ? value.toLocaleString('en-US') : String(value)}</div>
                </div>
              ))}
            </div>
          )}

          {selectedRelated.length > 0 && (
            <div className="mt-3">
              <div className="mb-1.5 text-[10px] uppercase tracking-[0.14em] text-cyan-600">Nearby context</div>
              <div className="space-y-1.5">
                {selectedRelated.map(signal => (
                  <button key={signal.id} onClick={() => setSelectedSignal(signal)} className="w-full rounded-md border border-cyan-900/35 bg-[#061417]/70 px-2.5 py-2 text-left text-[11px] text-cyan-200 transition-colors hover:border-cyan-700/60 hover:bg-cyan-500/[0.05]">
                    <span className="line-clamp-2">{signal.headline}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-3 flex items-center justify-between text-[11px] text-cyan-600">
            <span>{selectedSignal.publishedAt ? new Date(selectedSignal.publishedAt).toLocaleDateString() : 'No date'}</span>
            {selectedSignal.sourceUrl && (
              <a href={selectedSignal.sourceUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-md border border-cyan-900/50 px-2 py-1 font-medium text-cyan-300 hover:border-cyan-600 hover:text-cyan-100">
                Open source <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>

          {isSnapshotStale && (
            <div className="mt-3 flex gap-2 rounded-md border border-amber-500/20 bg-amber-500/10 p-2 text-[11px] leading-relaxed text-amber-100/90">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Snapshot is older than expected. The app still reads public R2 only.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OutbreakMapPage;
